import type { FileRejection } from 'react-dropzone';
import { FileImportAttempted, FileImportCompleted } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { type ImportResult } from 'src/apps/myFiles/app/hooks/useApiRequest';

export const ContentTypeDocx =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const ContentTypeText = 'text/plain';

export const isFileTypeText = (files: File[]): boolean => {
  return files.some((file) => file.type === ContentTypeText);
};

export const isFileTypeImage = (files: File[]): boolean => {
  return files.some((file) => file.type.startsWith('image/'));
};

export const isFileTypeDocx = (files: File[]): boolean => {
  return files.some((file) => file.type === ContentTypeDocx);
};

export const dropZoneAcceptedContentTypes = (
  withExtra: boolean
): Record<string, string[]> => {
  const acceptedContentTypes = {
    'application/pdf': ['.pdf'],
    'application/epub+zip': ['.epub'],
    //'image/*': ['.jpeg', '.png'],
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/png': ['.png'],
  } as Record<string, string[]>;

  if (withExtra) {
    acceptedContentTypes[ContentTypeText] = ['.txt'];
    acceptedContentTypes[ContentTypeDocx] = ['.docx'];
  }

  return acceptedContentTypes;
};

const getFileExtension = (file: File): string => {
  const parts = file.name.split('.');
  const ext = parts.length > 1 ? parts.pop() : undefined;
  return ext ? ext.toLowerCase() : 'unknown';
};

export const trackImportAttempted = ({
  componentLocation,
  acceptedFiles,
  rejectedFiles,
}: {
  componentLocation: string;
  acceptedFiles: File[];
  rejectedFiles: FileRejection[];
}) => {
  const acceptedFileTypes = [
    ...new Set(acceptedFiles.map(getFileExtension)),
  ].sort((a, b) => a.localeCompare(b));
  const rejectedFileTypes = [
    ...new Set(rejectedFiles.map((f) => getFileExtension(f.file))),
  ].sort((a, b) => a.localeCompare(b));
  tracker.trackEvent(
    new FileImportAttempted({
      component_location: componentLocation,
      file_count: acceptedFiles.length + rejectedFiles.length,
      accepted_file_count: acceptedFiles.length,
      rejected_file_count: rejectedFiles.length,
      accepted_file_types: acceptedFileTypes,
      rejected_file_types: rejectedFileTypes,
    })
  );
};

export const trackImportCompleted = ({
  componentLocation,
  importsAttempted,
  results,
}: {
  componentLocation: string;
  importsAttempted: number;
  results: ImportResult[];
}) => {
  const successCount = results.filter((r) => r.success).length;
  const failCount = results.length - successCount;
  const successFileTypes = Array.from(
    new Set(
      results
        .filter((r) => r.success && r.fileType)
        .map((r) => r.fileType as string)
    )
  ).sort((a, b) => a.localeCompare(b));
  tracker.trackEvent(
    new FileImportCompleted({
      component_location: componentLocation,
      file_count: importsAttempted,
      success_count: successCount,
      failure_count: failCount,
      success_file_types: successFileTypes,
    })
  );
};
